/**
 * ScopeChip — Global / Tenant catalog discriminator pill.
 *
 * Originally introduced by Story 059 (turbine-models) and graduated to
 * `@aeriosiq/ui` by Story 062 (component-models) per the rule of two.
 *
 * - `scope: 'global'` → outlined "Global" (text+border in
 *   `text.secondary`). Look, don't touch.
 * - `scope: 'tenant'` → filled "Tenant" (semantic-info fill +
 *   accent text). Actionable scope for tenant_admin / project_manager.
 *
 * The chip text "Global" / "Tenant" is the accessible label — Story
 * 059 AC10/AC11/AC18 + Story 062 AC2/AC10 all use
 * `getByText(/^Global$/, { exact: true })` / `getByText(/^Tenant$/,
 * { exact: true })`. No `aria-label` override.
 *
 * Tokens consumed: `state.info` (Tenant fill + accent text),
 * `text.secondary` (Global border + label), `surface.canvas` (Global
 * fill). No new tokens introduced by the rule-of-two graduation.
 */
import type { ReactElement } from "react";
import { Chip } from "@mui/material";

export function ScopeChip({
  scope,
}: {
  scope: "global" | "tenant";
}): ReactElement {
  if (scope === "global") {
    return (
      <Chip
        size="small"
        variant="outlined"
        label="Global"
        sx={(t) => ({
          fontWeight: 500,
          color: t.palette.text.secondary,
          borderColor: t.palette.divider,
        })}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Tenant"
      sx={(t) => ({
        fontWeight: 500,
        bgcolor:
          t.palette.mode === "dark"
            ? "rgba(41, 151, 255, 0.18)"
            : "rgba(0, 113, 227, 0.10)",
        color: t.palette.primary.main,
        border: "none",
      })}
    />
  );
}
